import React, { useEffect, useMemo, useState } from 'react';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import { managedFarmPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import styled from 'styled-components';
import { Button } from '../../../components/common/buttons/Button';
import { Chip } from '../../../components/common/chip/Chip';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { openSmsWithmanagedFarmInfo } from '../../../utils/opener/openSmsWithManagedFarmInfo.util';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { deleteTaskById, getTaskById } from '../../../utils/manager/managedFarmTaskManager.util';
import { IManagedFarmTask } from '../../../interfaces/ManagedFarm.interfaces';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';
import { ampli } from '../../../ampli';
import { openSimpleSmsToFieldOwner } from '../../../utils/opener/openSimpleSmsToFieldOwner.util';

const SIMPLE_SMS_TEXT: { key: string; message: string }[] = [
  { key: '1', message: '내일 작업하러 갑니다.' },
  { key: '2', message: '출발했습니다.' },
  { key: '3', message: '잠깐 자리 비웁니다.' },
  { key: '4', message: '작업지 도착했습니다.' },
];

export const ManagedFarmTaskDetailPage = () => {
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const { taskId } = useParams();
  const { openNotification } = useNotification();

  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState<boolean>(false);
  const [isSimpleSmsModalOpen, setIsSimpleSmsModalOpen] = useState<boolean>(false);
  const [task, setTask] = useState<IManagedFarmTask>();

  const isFromFormPage = searchParam.get('from') === 'form';

  const taskSchedule = useMemo<string | null>(() => {
    if (!task || !task.startDate || !task.endDate) {
      return null;
    }

    return `${format(task.startDate, 'yy.MM.dd')}~${format(task.endDate, 'yy.MM.dd')}`;
  }, [task]);

  const totalCheckpoint = useMemo<string>(() => {
    if (!task) {
      return '';
    }

    return `${task.recommendedCheckpoints ?? ''}\n${task.checkpoint ?? ''}`;
  }, [task]);

  const taskTitle = useMemo<string>(() => {
    if (!task) {
      return '';
    }

    const pageTitle = `${task.landInfo?.addressTrailingParts} ${
      task.cropType !== '기타' ? task.cropType : task.cropTypeEtc
    }`;

    return pageTitle;
  }, [task]);

  const contactWithPhone = () => {
    if (!task) {
      return;
    }

    ampli.pvmClickMangedFarmSimpleCall({
      field_owner_name: task.fieldOwnerName,
      field_owner_phone_number: task.fieldOwnerPhoneNumber,
      task_title: taskTitle,
    });
    window.location.href = `tel:${task.fieldOwnerPhoneNumber}`;
  };

  const contactWithSms = () => {
    if (!task) {
      return;
    }

    ampli.pvmClickSendManagedFarmTaskInfoButton({
      field_owner_name: task.fieldOwnerName,
      field_owner_phone_number: task.fieldOwnerPhoneNumber,
      task_title: taskTitle,
    });

    openSmsWithmanagedFarmInfo({
      fieldOwnerPhoneNumber: task.fieldOwnerPhoneNumber,
      fieldAddress: task.address ?? '',
      fieldMeasurement: String(task.landInfo?.areaInPyeong),
      taskStatus: task.status,
      taskType: task.taskType !== '기타' ? task.taskType : task.taskTypeEtc,
      equipment: task.equipment !== '기타' ? task.equipment : task.equipmentEtc,
      cropType: task.cropType !== '기타' ? task.cropType : task.cropTypeEtc,
      taskSchedule: taskSchedule ?? '',
      checkpoint: totalCheckpoint,
    });
  };
  const contactWithShortSms = () => {
    setIsSimpleSmsModalOpen(true);
  };

  const handleGreyButtonClick = () => {
    if (isFromFormPage) {
      ampli.pvmClickGoToMyManagedFarmTaskButton();
      navigate(managedFarmPageInfo.myTaskPage.path);
    } else {
      setIsDeleteTaskModalOpen(true);
    }
  };

  const handleTaskDelete = async () => {
    if (!task) {
      return;
    }

    try {
      await deleteTaskById(task.id);

      if (task.startDate) {
        ampli.pvmDeleteManagedFarmTask({
          task_title: taskTitle,
          task_start_date: format(task.startDate, 'yy.MM.dd'),
        });
      }
    } catch (error) {
      console.error('태스크 삭제 실패:', error);
    }

    navigate(managedFarmPageInfo.myTaskPage.path, { replace: true });
    openNotification({ message: '작업 삭제가 완료되었습니다.' });
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true, directlyPassedURL: managedFarmPageInfo.myTaskPage.path }} />,
    fixedFooter: (
      <FooterContainer>
        <BlurSection />

        <ButtonWrapper>
          <Button
            text={{ size: 'md', content: isFromFormPage ? '전체 작업으로 가기' : '작업 삭제하기' }}
            color={'filledGreyLight'}
            size={'md'}
            borderType={'square'}
            onClick={handleGreyButtonClick}
          />
        </ButtonWrapper>
        <ButtonWrapper>
          <Button
            text={{ size: 'md', content: '작업 내용 문자보내기' }}
            color={'filledPrimaryStrong'}
            size={'md'}
            borderType={'square'}
            onClick={contactWithSms}
          />
        </ButtonWrapper>
      </FooterContainer>
    ),
    fixedHeaderHeight: 48,
    fixedFooterHeight: 72,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (!taskId) {
      return;
    }

    const loadTask = async () => {
      try {
        const savedTask = await getTaskById(taskId);
        if (savedTask) {
          setTask(savedTask);
        }
      } catch (error) {
        console.error('태스크 로드 실패:', error);
      }
    };

    loadTask();
  }, [taskId]);

  if (!task) {
    // TODO : 빈 페이지 구현 PVM-3418
    return <span>{''}</span>;
  }

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.taskDetailPage.title}</title>
      </Helmet>
      <Container>
        <HeaderSection>
          <TaskTitle>{taskTitle}</TaskTitle>
          <Chip
            type={'square'}
            theme={'light'}
            color={task.status === '작업 예정' ? 'primary' : task.status === '작업 중' ? 'secondary' : 'grey'}
          >
            {task.status}
          </Chip>
        </HeaderSection>
        <SectionConatiner>
          <SectionTitle>{'필지 정보'}</SectionTitle>
          <SectionContentBackground>
            <FieldInfoContainer>
              <LargeText>{task.address}</LargeText>
              <FieldMeasurementContainer>
                <MediumText>{'평수'}</MediumText>
                <MediumText>{`${task.landInfo?.areaInPyeong}평 (${task.landInfo?.landArea}㎡)`}</MediumText>
              </FieldMeasurementContainer>
              <MapImageContainer>
                <MapImageWrapper>
                  <MapImage src={task.mapImage || ''} alt="캡처된 지적도" />
                </MapImageWrapper>
              </MapImageContainer>
            </FieldInfoContainer>
            <HR />
            <FieldOwnerContainer>
              <MediumText>{'필지 소유자'}</MediumText>
              <InfoRow>
                <SmallText>{'이름'}</SmallText>
                <SmallChip>
                  <SmallChipText className="sensitive-text">{task.fieldOwnerName}</SmallChipText>
                </SmallChip>
              </InfoRow>
              <InfoRow>
                <SmallText>{'전화번호'}</SmallText>
                <SmallChip>
                  <SmallChipText className="sensitive-text">{task.fieldOwnerPhoneNumber}</SmallChipText>
                </SmallChip>
              </InfoRow>
            </FieldOwnerContainer>
            <FieldOwnerContactSection>
              <ButtonWrapper>
                <Button
                  borderType={'square'}
                  color={'filledWhiteStrong'}
                  size={'md'}
                  text={{ content: '전화하기', size: 'md' }}
                  onClick={contactWithPhone}
                />
              </ButtonWrapper>
              <ButtonWrapper>
                <Button
                  borderType={'square'}
                  color={'filledWhiteStrong'}
                  size={'md'}
                  text={{ content: '간편 문자보내기', size: 'md' }}
                  onClick={contactWithShortSms}
                />
              </ButtonWrapper>
            </FieldOwnerContactSection>
          </SectionContentBackground>
        </SectionConatiner>

        <SectionConatiner>
          <SectionTitle>{'작업 정보'}</SectionTitle>
          <SectionContentBackground>
            <TaskDetailInfoContainer>
              <MediumText>{'작업 상세 정보'}</MediumText>
              <InfoRow>
                <SmallText>{'작업 종류'}</SmallText>
                <SmallText>{task.taskType !== '기타' ? task.taskType : task.taskTypeEtc}</SmallText>
              </InfoRow>
              <InfoRow>
                <SmallText>{'사용 장비'}</SmallText>
                <SmallText>{task.equipment !== '기타' ? task.equipment : task.equipmentEtc}</SmallText>
              </InfoRow>
              <InfoRow>
                <SmallText>{'작물 종류'}</SmallText>
                <SmallText>{task.cropType !== '기타' ? task.cropType : task.cropTypeEtc}</SmallText>
              </InfoRow>
              <InfoRow>
                <SmallText>{'예상 작업 시간'}</SmallText>
                <SmallChip>
                  <SmallChipText>{taskSchedule}</SmallChipText>
                </SmallChip>
              </InfoRow>
            </TaskDetailInfoContainer>

            <HR />

            <TaskCheckpointContainer>
              <MediumText>{'확인할 사항'}</MediumText>
              <TaskCheckpoint>
                <TaskCheckpointText>{totalCheckpoint}</TaskCheckpointText>
              </TaskCheckpoint>
            </TaskCheckpointContainer>
          </SectionContentBackground>
        </SectionConatiner>
      </Container>

      <PopupConfirmModal
        isShow={isDeleteTaskModalOpen}
        hasCloseButton={false}
        title={'작업을 삭제하시겠습니까?'}
        buttonArray={[
          {
            text: '삭제하기',
            onClick: handleTaskDelete,
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setIsDeleteTaskModalOpen(false),
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isSimpleSmsModalOpen}
        hasCloseButton={false}
        title={'필지 소유자에게\n보낼 문자를 선택해주세요.'}
        element={
          <SimpleMessageButtonContainer>
            {SIMPLE_SMS_TEXT.map((smsText) => (
              <Button
                key={smsText.key}
                borderType={'square'}
                color={'filledGreyLight'}
                size={'md'}
                text={{ size: 'md', content: smsText.message }}
                onClick={() => {
                  ampli.pvmClickMangedFarmSimpleSms({
                    field_owner_name: task.fieldOwnerName,
                    field_owner_phone_number: task.fieldOwnerPhoneNumber,
                    task_title: taskTitle,
                    message: smsText.message,
                  });

                  openSimpleSmsToFieldOwner({
                    fieldOwnerPhoneNumber: task.fieldOwnerPhoneNumber,
                    message: smsText.message,
                  });
                }}
              />
            ))}
          </SimpleMessageButtonContainer>
        }
        buttonArray={[
          {
            text: '닫기',
            onClick: () => setIsSimpleSmsModalOpen(false),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const FooterContainer = styled.div`
  width: 100%;

  padding: 0px 16px 32px 16px;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const BlurSection = styled.div`
  width: 100%;
  max-width: 512px;
  height: 40px;

  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);

  position: absolute;
  left: 0px;
  top: -40px;

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);
`;

const Container = styled.div`
  padding: 16px 16px 60px 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const TaskTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const SectionConatiner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const SectionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 13.6px */
`;

const SectionContentBackground = styled.div`
  padding: 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  border-radius: 8px;
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const FieldInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const LargeText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const FieldMeasurementContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const MediumText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
`;

const FieldOwnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const HR = styled.hr`
  width: 100%;

  stroke-width: 1px;
  stroke: var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const SmallText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const SmallChip = styled.div`
  display: flex;
  min-width: 120px;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 4px;
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const SmallChipText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
`;

const FieldOwnerContactSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const ButtonWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
`;

const TaskDetailInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TaskCheckpointContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TaskCheckpoint = styled.div`
  width: 100%;
  min-height: 120px;
  padding: 10px 16px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TaskCheckpointText = styled.pre`
  height: 100%;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */

  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const MapImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`;

const MapImageWrapper = styled.div`
  width: 310px;
  height: 175px;

  display: flex;
  border: 1px solid #eee;
  justify-content: center;
  align-items: center;
`;

const MapImage = styled.img`
  width: 100%;
  height: 100%;
`;

const SimpleMessageButtonContainer = styled.div`
  max-height: 400px;

  padding-bottom: 8px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-radius: var(--radius-none, 0px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;
